import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import MessageIcon from '@mui/icons-material/Message'
import {
  useGridApiRef,
} from '@mui/x-data-grid'

import { Message } from '../../../services/campaignApi'
import { useCampaignMessageStats } from '../../../hooks/campaigns/useCampaignMessageStats'
import { useMessageColumns } from './useMessageColumns'
import { MobileFilterDrawer } from './MobileFilterDrawer'
import { MobileMessageList } from './MobileMessageList'
import { DesktopMessageList } from './DesktopMessageList'
import { TableSearchBar } from '../../common/table'
import EmptyState from '../../EmptyState'

export interface CampaignMessageStatsTabProps {
  campaignId: string
  onOpenConversation: (msg: Message) => void
  isRefreshing?: boolean
}


export const CampaignMessageStatsTab: React.FC<CampaignMessageStatsTabProps> = ({
  campaignId,
  onOpenConversation,
  isRefreshing,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const messageStats = useCampaignMessageStats(campaignId, { isRefreshing })
  const {
    messages,
    isLoading,
    totalMessages,
    paginationModel,
    setPaginationModel,
    sortModel,
    setSortModel,
    searchInput,
    setSearchInput,
    dynamicFilters,
    setDynamicFilters,
    toggleFilterOption,
    clearColumnFilter,
    isFiltered,
    handleClearFilters,
    activeFilterCount,
    sortBy: currentSortField = '',
    sortOrder: currentSortOrder,
  } = messageStats

  // DataGrid API ref for native menu control & auto-close on scroll
  const apiRef = useGridApiRef()
  const [mobileFilterDrawerOpen, setMobileFilterDrawerOpen] = useState(false)
  const [expandedMessageId, setExpandedMessageId] = useState<string | null>(null)

  const handleToggleExpand = (e: React.MouseEvent, msgId: string) => {
    e.stopPropagation()
    setExpandedMessageId(prev => (prev === msgId ? null : msgId))
  }

  const handleSelectSortField = (field: string) => {
    if (!field || field === currentSortField) {
      setSortModel([])
    } else {
      setSortModel([{ field, sort: currentSortOrder }])
    }
  }

  const handleSelectSortOrder = (order: 'asc' | 'desc') => {
    const field = currentSortField || 'sent_at'
    setSortModel([{ field, sort: order }])
  }

  const handleResetMobileFilters = () => {
    handleClearFilters()
    setSortModel([])
  }


  // Automatically close open column menu on window/container scroll
  useEffect(() => {
    const handleScroll = (event: Event) => {
      const target = event.target as HTMLElement
      if (
        target &&
        (target.closest?.('.MuiDataGrid-menu') || target.closest?.('.MuiDrawer-paper'))
      ) {
        return
      }

      if (mobileFilterDrawerOpen) {
        return
      }

      apiRef.current?.hideColumnMenu?.()
      apiRef.current?.hidePreferences?.()
    }

    window.addEventListener('scroll', handleScroll, { passive: true, capture: true })
    return () => {
      window.removeEventListener('scroll', handleScroll, { capture: true })
    }
  }, [apiRef, mobileFilterDrawerOpen])

  const messageColumns = useMessageColumns({
    dynamicFilters,
    apiRef,
    onOpenConversation,
  })

  const renderLoadingOverlay = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 400 }}>
      <CircularProgress size={36} />
    </Box>
  )

  const renderNoRowsOverlay = () => {
    if (isLoading) return null
    return (
      <EmptyState
        icon={<MessageIcon />}
        title={isFiltered ? 'No matching messages' : 'No messages found'}
        description={
          isFiltered
            ? 'No messages match your active search or filters. Try clearing your filters.'
            : 'Messages will appear here once delivery begins.'
        }
        action={
          isFiltered ? (
            <Button variant="outlined" size="small" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          ) : undefined
        }
        minHeight={420}
      />
    )
  }

  return (
    <Box sx={{ minHeight: { xs: 520, md: 580 } }}>
      {/* Search Bar & Mobile Filter Trigger */}
      <TableSearchBar
        isMobile={isMobile}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        activeFilterCount={activeFilterCount}
        onOpenMobileFilters={() => setMobileFilterDrawerOpen(true)}
        placeholder="Search by name or phone..."
        mobilePlaceholder="Name or phone..."
      />

      {/* Mobile Filter & Sort Bottom Sheet Drawer */}
      <MobileFilterDrawer
        open={isMobile && mobileFilterDrawerOpen}
        onOpen={() => setMobileFilterDrawerOpen(true)}
        onClose={() => setMobileFilterDrawerOpen(false)}
        activeFilterCount={activeFilterCount}
        handleResetMobileFilters={handleResetMobileFilters}
        dynamicFilters={dynamicFilters}
        toggleFilterOption={toggleFilterOption}
        clearColumnFilter={clearColumnFilter}
        currentSortField={currentSortField}
        setSortModel={setSortModel}
        handleSelectSortField={handleSelectSortField}
        currentSortOrder={currentSortOrder}
        handleSelectSortOrder={handleSelectSortOrder}
        isLoading={isLoading}
        totalMessages={totalMessages}
        campaignId={campaignId}
      />



      {/* Mobile List View or Desktop DataGrid */}
      {isMobile ? (
        <MobileMessageList
          messages={messages}
          isLoading={isLoading}
          totalMessages={totalMessages}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          expandedMessageId={expandedMessageId}
          handleToggleExpand={handleToggleExpand}
          onOpenConversation={onOpenConversation}
          renderNoRowsOverlay={renderNoRowsOverlay}
          renderLoadingOverlay={renderLoadingOverlay}
        />
      ) : (
        <DesktopMessageList
          apiRef={apiRef}
          messages={messages}
          messageColumns={messageColumns}
          isLoading={isLoading}
          totalMessages={totalMessages}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          sortModel={sortModel}
          setSortModel={setSortModel}
          campaignId={campaignId}
          dynamicFilters={dynamicFilters}
          setDynamicFilters={setDynamicFilters}
          renderNoRowsOverlay={renderNoRowsOverlay}
          renderLoadingOverlay={renderLoadingOverlay}
        />
      )}
    </Box>
  )
}

export default CampaignMessageStatsTab
